import type { ClaudeSubscriptionAuthStatus } from "@t3tools/contracts";

export type ClaudeCloudExecutionTarget = "local" | "cloud" | "remote";

export function resolveClaudeCloudExecutionTarget(input: {
  readonly placement: "local" | "cloud" | undefined;
  readonly sameDevice: boolean;
}): ClaudeCloudExecutionTarget {
  if (input.placement === "cloud") return "cloud";
  return input.sameDevice ? "local" : "remote";
}

export type ClaudeSubscriptionConnectionStatus = "loading" | "connected" | "disconnected" | "error";

/**
 * The web surface intentionally consumes a small view model instead of a
 * provider/runtime contract. The cloud control plane can adapt its response
 * to this shape without making the settings page know how credentials are
 * stored or injected.
 */
export interface ClaudeSubscriptionConnection {
  readonly status: ClaudeSubscriptionConnectionStatus;
  readonly message?: string | null;
}

export interface ClaudeSubscriptionPresentation {
  readonly label: string;
  readonly description: string;
  readonly tone: "muted" | "success" | "error" | "warning";
}

/**
 * Adapt the redacted environment endpoint to the small view model rendered by
 * Settings. The endpoint never returns an account identifier, so the UI does
 * not attempt to infer one from a provider snapshot.
 */
export function presentClaudeSubscriptionAuthStatus(
  status: ClaudeSubscriptionAuthStatus,
): ClaudeSubscriptionConnection {
  switch (status.state) {
    case "connected":
      return { status: "connected" };
    case "disconnected":
      return {
        status: status.cliAvailable ? "disconnected" : "error",
        ...(status.cliAvailable
          ? {}
          : { message: "Claude Code CLI is not available in this environment." }),
      };
    case "different-authentication":
      return {
        status: "error",
        message:
          "Claude Code is using a different authentication method. Sign in with a Claude subscription to use this provider connection.",
      };
    case "unknown":
      return {
        status: "error",
        message: status.cliAvailable
          ? "Claude Code subscription status is unavailable."
          : "Claude Code CLI is not available in this environment.",
      };
  }
}

export function presentClaudeSubscriptionConnection(
  connection: ClaudeSubscriptionConnection,
): ClaudeSubscriptionPresentation {
  switch (connection.status) {
    case "loading":
      return {
        label: "Checking connection",
        description: "Checking whether your Claude Code subscription is available here.",
        tone: "muted",
      };
    case "connected":
      return {
        label: "Connected",
        description: "Your Claude Code subscription is available in this environment.",
        tone: "success",
      };
    case "error":
      return {
        label: "Connection error",
        description:
          connection.message ?? "We could not verify the Claude Code subscription connection.",
        tone: "error",
      };
    case "disconnected":
      return {
        label: "Not connected",
        description: "Connect a Claude Code subscription before starting tasks here.",
        tone: "muted",
      };
  }
}

export function canRunClaudeCloudTask(input: {
  readonly target: ClaudeCloudExecutionTarget;
  readonly connection: ClaudeSubscriptionConnection;
}): boolean {
  return input.target !== "cloud" || input.connection.status === "connected";
}

export function cloudExecutionTargetLabel(target: ClaudeCloudExecutionTarget): string {
  if (target === "cloud") return "Cloud";
  if (target === "remote") return "Remote";
  return "Local";
}
